import { format, isValid, parse } from 'date-fns';
import type { ReactNode } from 'react';
import type { Project } from '@/api/client';
import {
  formatNoEdits,
  getReviewCheckTypeLabel,
  REVIEW_TABLE_CELL_EMPTY,
} from '@/utils/reviewCheckType';
import { highlightPlainText } from '@/utils/highlightSearchTerms';

function formatDateRu(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const s = value.trim();
  const d = parse(s.slice(0, 10), 'yyyy-MM-dd', new Date());
  if (isValid(d)) return format(d, 'dd.MM.yyyy');
  const d2 = new Date(s);
  return !isNaN(d2.getTime()) ? format(d2, 'dd.MM.yyyy') : s;
}

const LABELS: Record<string, string> = {
  review_check_type: 'Тип проверки',
  review_deadline: 'Дедлайн проверки',
  corrections_number: 'Кол-во правок',
  no_edits: 'Без правок',
};

interface ReviewComboCellProps {
  project: Project;
  columnIds: string[];
  searchHighlightWords?: string[];
}

export function ReviewComboCell({ project, columnIds, searchHighlightWords }: ReviewComboCellProps) {
  const hl = (text: string) =>
    searchHighlightWords?.length ? highlightPlainText(text, searchHighlightWords) : text;

  const typeText = getReviewCheckTypeLabel(project.review_check_type);
  const deadlineStr = formatDateRu(project.review_deadline);
  const correctionsStr =
    project.corrections_number != null && Number.isFinite(Number(project.corrections_number))
      ? String(project.corrections_number)
      : null;
  const subText = formatNoEdits(project.no_edits);

  const cellDisplay = (raw: string | null): ReactNode =>
    raw != null ? (
      hl(raw)
    ) : (
      <span className="projects-table__empty-value">{hl(REVIEW_TABLE_CELL_EMPTY)}</span>
    );

  return (
    <div className="projects-table__combo-cell projects-table__labeled-cell projects-table__labeled-cell_has-content">
      {columnIds.map((id) => {
        const label = LABELS[id];
        if (!label) return null;
        const raw =
          id === 'review_check_type'
            ? typeText
            : id === 'review_deadline'
              ? deadlineStr
              : id === 'corrections_number'
                ? correctionsStr
                : id === 'no_edits'
                  ? subText
                  : null;
        return (
          <span
            key={id}
            className={
              id === 'review_check_type' ? 'projects-table__combo-line projects-table__combo-line_review-check-type' : undefined
            }
          >
            <span className="projects-table__labeled-cell-label">{hl(`${label}:`)}</span> {cellDisplay(raw)}
          </span>
        );
      })}
    </div>
  );
}
